import React, { useState, useEffect } from "react"
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Tooltip,
  Button,
  Input,
  FormGroup,
  Label,
} from "reactstrap"
import { useTranslation } from "react-i18next"
import Select from "react-select"
import { AddMedicineFormData } from "../medicineTypes"
import { useListQuery } from "helpers/http-client/hooks/useCrud"
import { API_ENDPOINTS } from "helpers/http-client/httpConstants"

interface ConfirmDetailsProps {
  formData: AddMedicineFormData
  onBack: () => void
  onSubmit: (updatedData: AddMedicineFormData) => void
  isUrdu: boolean
}

const ConfirmDetails: React.FC<ConfirmDetailsProps> = ({
  formData,
  onBack,
  onSubmit,
  isUrdu,
}) => {
  const { t } = useTranslation()
  const [editData, setEditData] = useState<AddMedicineFormData>(formData)
  const [editingField, setEditingField] = useState<string | null>(null)
  const [tooltips, setTooltips] = useState<Record<string, boolean>>({})

  const {
    data: manufacturerOptions,
    isLoading: isLoadingManufacturers,
    isError: isErrorManufacturers,
  } = useListQuery(API_ENDPOINTS.MANUFACTURERS)
  const {
    data: categoryOptions,
    isLoading: isLoadingCategories,
    isError: isErrorCategories,
  } = useListQuery(API_ENDPOINTS.CATEGORIES)
  const {
    data: sideEffectsData,
    isLoading: isLoadingSideEffects,
    isError: isErrorSideEffects,
  } = useListQuery(API_ENDPOINTS.SIDE_EFFECTS)
  const {
    data: ingredientOptions,
    isLoading: isLoadingIngredients,
    isError: isErrorIngredients,
  } = useListQuery(API_ENDPOINTS.ACTIVE_INGREDIENTS)

  useEffect(() => {
    setEditData(formData)
  }, [formData])

  const toggleTooltip = (key: string) => {
    setTooltips(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const handleInputChange = (key: string, value: any) => {
    setEditData(prev => ({ ...prev, [key]: value }))
  }

  const handleSubmit = () => {
    console.log("Submitting data:", editData)
    return
    onSubmit(editData)
  }

  const showValue = (key: string, val: any) => {
    if (val === undefined || val === null || val === "") {
      return <span className="text-muted fst-italic">{t("N/A")}</span>
    }

    if (key === "manufacturerId") {
      const manufacturer = manufacturerOptions?.find(
        (m: any) => m.manufacturerId === val
      )
      return manufacturer ? manufacturer.name : val
    }

    if (key === "categoryId") {
      const category = categoryOptions?.find((c: any) => c.categoryId === val)
      return category ? category.name : val
    }

    if (key === "medicineSideEffects") {
      return (
        val
          ?.map((se: any) => {
            const sideEffect = sideEffectsData?.find(
              (s: any) => s.sideEffectId === se.sideEffectId
            )
            return sideEffect
              ? `${sideEffect.name}${se.notes ? ` (${se.notes})` : ""}`
              : se.sideEffectId
          })
          .join(", ") || (
          <span className="text-muted fst-italic">{t("N/A")}</span>
        )
      )
    }

    if (key === "medicineIngredients") {
      return (
        val
          ?.map((ing: any) => {
            const ingredient = ingredientOptions?.find(
              (i: any) => i.ingredientId === ing.ingredientId
            )
            return ingredient
              ? `${ingredient.name}${ing.concentration ? ` (${ing.concentration})` : ""}`
              : ing.ingredientId
          })
          .join(", ") || (
          <span className="text-muted fst-italic">{t("N/A")}</span>
        )
      )
    }

    return val
  }

  const booleanText = (val: boolean | undefined, key: string) => {
    if (editingField === key) {
      return (
        <Input
          type="select"
          value={val === undefined ? "" : String(val)}
          onChange={e => handleInputChange(key, e.target.value === "true")}
          onBlur={() => setEditingField(null)}
          autoFocus
        >
          <option value="">{t("N/A")}</option>
          <option value="true">{t("Yes")}</option>
          <option value="false">{t("No")}</option>
        </Input>
      )
    }
    return val === true ? (
      <span className="badge bg-success">{t("Yes")}</span>
    ) : val === false ? (
      <span className="badge bg-danger">{t("No")}</span>
    ) : (
      showValue(key, val)
    )
  }

  const renderInput = (key: string, value: any, type: string) => {
    if (editingField !== key) {
      return (
        <div className="cursor-pointer" onClick={() => setEditingField(key)}>
          {type === "boolean" ? booleanText(value, key) : showValue(key, value)}
        </div>
      )
    }

    if (key === "manufacturerId") {
      return (
        <Select
          inputId="manufacturerId"
          name="manufacturerId"
          options={manufacturerOptions?.map((m: any) => ({
            value: m.manufacturerId,
            label: m.name,
          }))}
          placeholder={t("Select Manufacturer")}
          value={
            value
              ? {
                  value: value,
                  label:
                    manufacturerOptions?.find(
                      (m: any) => m.manufacturerId === value
                    )?.name || value,
                }
              : null
          }
          onChange={option => {
            handleInputChange("manufacturerId", option ? option.value : "")
            setEditingField(null)
          }}
          onBlur={() => setEditingField(null)}
          styles={{
            control: base => ({
              ...base,
              textAlign: isUrdu ? "right" : "left",
            }),
            singleValue: base => ({
              ...base,
              textAlign: isUrdu ? "right" : "left",
              direction: isUrdu ? "rtl" : "ltr",
            }),
          }}
          isLoading={isLoadingManufacturers}
          isDisabled={isErrorManufacturers}
          isRtl={isUrdu}
          isClearable
        />
      )
    }

    if (key === "categoryId") {
      return (
        <Select
          inputId="categoryId"
          name="categoryId"
          options={categoryOptions?.map((c: any) => ({
            value: c.categoryId,
            label: c.name,
          }))}
          placeholder={t("Select Category")}
          value={
            value
              ? {
                  value: value,
                  label:
                    categoryOptions?.find((c: any) => c.categoryId === value)
                      ?.name || value,
                }
              : null
          }
          onChange={option => {
            handleInputChange("categoryId", option ? option.value : "")
            setEditingField(null)
          }}
          onBlur={() => setEditingField(null)}
          styles={{
            control: base => ({
              ...base,
              textAlign: isUrdu ? "right" : "left",
            }),
            singleValue: base => ({
              ...base,
              textAlign: isUrdu ? "right" : "left",
              direction: isUrdu ? "rtl" : "ltr",
            }),
          }}
          isLoading={isLoadingCategories}
          isDisabled={isErrorCategories}
          isRtl={isUrdu}
          isClearable
        />
      )
    }

    if (key === "medicineSideEffects") {
      return (
        <Select
          isMulti
          name="medicineSideEffects"
          options={
            isErrorSideEffects
              ? []
              : (sideEffectsData || []).map((se: any) => ({
                  value: se.sideEffectId,
                  label: se.name,
                  data: se,
                }))
          }
          placeholder={t("Select side effects")}
          value={value?.map((se: any) => ({
            value: se.sideEffectId,
            label:
              sideEffectsData?.find(
                (s: any) => s.sideEffectId === se.sideEffectId
              )?.name || se.sideEffectId,
          }))}
          onChange={selected => {
            handleInputChange(
              "medicineSideEffects",
              selected.map((s: any) => ({ sideEffectId: s.value }))
            )
            setEditingField(null)
          }}
          onBlur={() => setEditingField(null)}
          isLoading={isLoadingSideEffects}
          isDisabled={isErrorSideEffects}
          styles={{
            control: base => ({
              ...base,
              textAlign: isUrdu ? "right" : "left",
            }),
            multiValueLabel: base => ({
              ...base,
              whiteSpace: "normal",
            }),
          }}
          isRtl={isUrdu}
          isClearable
          formatOptionLabel={({ label, data }) => (
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={{ fontWeight: "600" }}>{label}</div>
              <div
                style={{ display: "flex", gap: 8, fontSize: 12, marginTop: 2 }}
              >
                <span
                  style={{
                    backgroundColor: (() => {
                      switch (data?.severity?.toLowerCase()) {
                        case "mild":
                          return "#28a745"
                        case "moderate":
                          return "#ffc107"
                        case "severe":
                          return "#dc3545"
                        default:
                          return "#6c757d"
                      }
                    })(),
                    color: "white",
                    padding: "2px 6px",
                    borderRadius: "10px",
                    fontWeight: "bold",
                    textTransform: "capitalize",
                  }}
                >
                  {data?.severity || "N/A"}
                </span>
                <span>{data?.frequency || "N/A"}</span>
              </div>
              <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
                {data?.description || "No description"}
              </div>
            </div>
          )}
        />
      )
    }

    if (key === "medicineIngredients") {
      return (
        <Select
          isMulti
          name="medicineIngredients"
          options={
            isErrorIngredients
              ? []
              : (ingredientOptions || []).map((ing: any) => ({
                  value: ing.ingredientId,
                  label: ing.name,
                  data: ing,
                }))
          }
          placeholder={t("Select Active Ingredients")}
          value={value?.map((ing: any) => ({
            value: ing.ingredientId,
            label:
              ingredientOptions?.find(
                (i: any) => i.ingredientId === ing.ingredientId
              )?.name || ing.ingredientId,
            data: ing,
          }))}
          onChange={selected => {
            handleInputChange(
              "medicineIngredients",
              selected.map((s: any) => ({
                ingredientId: s.value,
                concentration:
                  value?.find((i: any) => i.ingredientId === s.value)
                    ?.concentration || "",
              }))
            )
            setEditingField(null)
          }}
          onBlur={() => setEditingField(null)}
          isLoading={isLoadingIngredients}
          isDisabled={isErrorIngredients}
          styles={{
            control: base => ({
              ...base,
              textAlign: isUrdu ? "right" : "left",
            }),
            multiValueLabel: base => ({
              ...base,
              whiteSpace: "normal",
            }),
          }}
          isRtl={isUrdu}
          isClearable
          formatOptionLabel={({ label, data }) => (
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={{ fontWeight: "600" }}>{label}</div>
              {data?.concentration && (
                <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                  {t("Concentration")}: {data.concentration}
                </div>
              )}
            </div>
          )}
        />
      )
    }

    const commonProps = {
      autoFocus: true,
      onBlur: () => setEditingField(null),
      className: "form-control",
      value: value || "",
      onChange: (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      ) => handleInputChange(key, e.target.value),
    }

    switch (type) {
      case "boolean":
        return booleanText(value, key)
      case "number":
        return <Input type="number" {...commonProps} />
      case "date":
        return <Input type="date" {...commonProps} />
      case "textarea":
        return <Input type="textarea" rows={3} {...commonProps} />
      default:
        return <Input type="text" {...commonProps} />
    }
  }

  const detailItems: Array<[string, string, any, string, string, string]> = [
    [
      t("Medicine Name"),
      "name",
      editData.name,
      t("Brand name of the medicine."),
      "fas fa-capsules",
      "text",
    ],
    [
      t("Alias"),
      "alias",
      editData.alias,
      t("Alternative name or code."),
      "fas fa-tag",
      "text",
    ],
    [
      t("Generic Name"),
      "genericName",
      editData.genericName,
      t("Generic or active ingredient name."),
      "fas fa-prescription",
      "text",
    ],
    [
      t("Description"),
      "description",
      editData.description,
      t("Brief description."),
      "fas fa-info-circle",
      "textarea",
    ],
    [
      t("Dosage Form"),
      "dosageForm",
      editData.dosageForm,
      t("Form of the medicine (e.g., Tablet)."),
      "fas fa-tablets",
      "text",
    ],
    [
      t("Strength"),
      "strength",
      editData.strength,
      t("Dosage strength (e.g., 500 mg)."),
      "fas fa-weight",
      "text",
    ],
    [
      t("Route of Administration"),
      "routeOfAdministration",
      editData.routeOfAdministration,
      t("Method of administration."),
      "fas fa-syringe",
      "text",
    ],
    [
      t("Medicine Type"),
      "medicineType",
      editData.medicineType,
      t("Type or category of medicine."),
      "fas fa-vial",
      "text",
    ],
    [
      t("Prescription Required"),
      "prescriptionRequired",
      editData.prescriptionRequired,
      t("Is a prescription required?"),
      "fas fa-file-prescription",
      "boolean",
    ],
    [
      t("Item Alert"),
      "itemAlert",
      editData.itemAlert,
      t("Low-stock alerts enabled?"),
      "fas fa-bell",
      "boolean",
    ],
    [
      t("Allow Due"),
      "allowDue",
      editData.allowDue,
      t("Allow due payments?"),
      "fas fa-credit-card",
      "boolean",
    ],
    [
      t("Generate Purchase Order"),
      "generatePO",
      editData.generatePO,
      t("Generate purchase orders automatically?"),
      "fas fa-file-invoice",
      "boolean",
    ],
    [
      t("Is Active"),
      "isActive",
      editData.isActive,
      t("Is the medicine active?"),
      "fas fa-check-circle",
      "boolean",
    ],
    [
      t("Printable"),
      "printable",
      editData.printable,
      t("Is printable?"),
      "fas fa-print",
      "boolean",
    ],
    [
      t("Manufacturer"),
      "manufacturerId",
      editData.manufacturerId,
      t("Manufacturer of the medicine."),
      "fas fa-industry",
      "text",
    ],
    [
      t("Category"),
      "categoryId",
      editData.categoryId,
      t("Category of medicine."),
      "fas fa-folder",
      "text",
    ],
    [
      t("Class"),
      "class",
      editData.class,
      t("Class of medicine."),
      "fas fa-layer-group",
      "text",
    ],
    [
      t("Active Ingredients"),
      "medicineIngredients",
      editData.medicineIngredients,
      t("Active ingredients and their concentrations."),
      "fas fa-flask",
      "text",
    ],
    [
      t("Pieces in Packing"),
      "piecesInPacking",
      editData.piecesInPacking,
      t("Number of pieces in a packing."),
      "fas fa-box",
      "number",
    ],
    [
      t("Purchase Price"),
      "purchasePrice",
      editData.purchasePrice,
      t("Purchase price per package."),
      "fas fa-dollar-sign",
      "number",
    ],
    [
      t("Sales Price"),
      "salesPrice",
      editData.salesPrice,
      t("Selling price per package."),
      "fas fa-tag",
      "number",
    ],
    [
      t("Retail Price"),
      "retailPrice",
      editData.retailPrice,
      t("Retail price."),
      "fas fa-shopping-cart",
      "number",
    ],
    [
      t("Max Sale Discount (%)"),
      "maxSaleDiscount",
      editData.maxSaleDiscount,
      t("Maximum sale discount allowed."),
      "fas fa-percentage",
      "number",
    ],
    [
      t("Pack Sales Tax (%)"),
      "packSalesTax",
      editData.packSalesTax,
      t("Sales tax applicable on the package."),
      "fas fa-receipt",
      "number",
    ],
    [
      t("Quantity"),
      "quantity",
      editData.quantity,
      t("Available quantity."),
      "fas fa-warehouse",
      "number",
    ],
    [
      t("Batch Number"),
      "batchNumber",
      editData.batchNumber,
      t("Batch or lot number."),
      "fas fa-barcode",
      "text",
    ],
    [
      t("Expiry Date"),
      "expiryDate",
      editData.expiryDate,
      t("Expiry date."),
      "fas fa-calendar-times",
      "date",
    ],
    [
      t("Currency"),
      "currency",
      editData.currency,
      t("Currency of the prices."),
      "fas fa-money-bill",
      "text",
    ],
    [
      t("Country"),
      "country",
      editData.country,
      t("Country of origin."),
      "fas fa-globe",
      "text",
    ],
    [
      t("Package Size ID"),
      "packageSizeId",
      editData.packageSizeId,
      t("Package size identifier."),
      "fas fa-ruler",
      "text",
    ],
    [
      t("Storage Conditions"),
      "storageConditions",
      editData.storageConditions,
      t("Recommended storage conditions."),
      "fas fa-thermometer",
      "textarea",
    ],
    [
      t("Shelf Life (Months)"),
      "shelfLifeMonths",
      editData.shelfLifeMonths,
      t("Duration before expiry (months)."),
      "fas fa-hourglass",
      "number",
    ],
    [
      t("Approval Status"),
      "approvalStatus",
      editData.approvalStatus,
      t("Regulatory approval status."),
      "fas fa-stamp",
      "text",
    ],
    [
      t("Side Effects"),
      "medicineSideEffects",
      editData.medicineSideEffects,
      t("Known side effects."),
      "fas fa-exclamation-triangle",
      "text",
    ],
  ]

  const basicInfoKeys = [
    "name",
    "alias",
    "genericName",
    "description",
    "dosageForm",
    "strength",
    "routeOfAdministration",
    "medicineType",
    "prescriptionRequired",
    "itemAlert",
    "allowDue",
    "generatePO",
    "isActive",
    "printable",
  ]

  const classificationKeys = [
    "manufacturerId",
    "categoryId",
    "class",
    "medicineIngredients",
  ]
  const inventoryPricingKeys = [
    "piecesInPacking",
    "purchasePrice",
    "salesPrice",
    "retailPrice",
    "maxSaleDiscount",
    "packSalesTax",
    "quantity",
    "batchNumber",
    "expiryDate",
    "currency",
    "country",
    "packageSizeId",
  ]

  const additionalKeys = [
    "storageConditions",
    "shelfLifeMonths",
    "approvalStatus",
    "medicineSideEffects",
  ]

  const basicInfoItems = detailItems.filter(([_, key]) =>
    basicInfoKeys.includes(key)
  )
  const classificationItems = detailItems.filter(([_, key]) =>
    classificationKeys.includes(key)
  )
  const inventoryPricingItems = detailItems.filter(([_, key]) =>
    inventoryPricingKeys.includes(key)
  )
  const additionalDetailsItems = detailItems.filter(([_, key]) =>
    additionalKeys.includes(key)
  )

  const renderSection = (
    title: string,
    items: typeof basicInfoItems,
    icon: string
  ) => (
    <Card className="mb-4">
      <CardHeader className="bg-light d-flex align-items-center">
        <i className={`${icon} me-2 text-primary`}></i>
        <strong>{title}</strong>
      </CardHeader>
      <CardBody>
        <Row>
          {items.map(([label, key, value, tip, iconClass, type]) => (
            <Col md="4" sm="6" className="mb-3" key={key}>
              <FormGroup>
                <Label className="form-label fw-bold d-flex align-items-center">
                  <i className={`${iconClass} me-2 text-secondary`}></i>
                  {label}
                  <span
                    id={`tooltip-${key}`}
                    className="ms-1 text-info"
                    style={{ cursor: "pointer" }}
                  >
                    ?
                  </span>
                </Label>
                <Tooltip
                  target={`tooltip-${key}`}
                  isOpen={tooltips[key]}
                  toggle={() => toggleTooltip(key)}
                >
                  {tip}
                </Tooltip>
                {renderInput(key, value, type)}
              </FormGroup>
            </Col>
          ))}
        </Row>
      </CardBody>
    </Card>
  )

  return (
    <Container className={`py-5 ${isUrdu ? "text-end" : ""}`}>
      <div className="text-center mb-4">
        <i className="fas fa-check-circle text-success fa-3x mb-2"></i>
        <h2 className="fw-bold">{t("Confirm Medicine Details")}</h2>
        <p className="text-muted">
          {t("Review and edit details before submission.")}
        </p>
      </div>

      {renderSection(t("Basic Info"), basicInfoItems, "fas fa-info-circle")}
      {renderSection(t("Classification"), classificationItems, "fas fa-tags")}
      {renderSection(
        t("Inventory and Pricing"),
        inventoryPricingItems,
        "fas fa-box-open"
      )}
      {renderSection(
        t("Additional Details"),
        additionalDetailsItems,
        "fas fa-clipboard-list"
      )}

      <div className="d-flex justify-content-between mt-4">
        <Button color="secondary" onClick={onBack}>
          {t("Back")}
        </Button>
        <Button color="primary" onClick={handleSubmit}>
          {t("Save")}
        </Button>
      </div>
    </Container>
  )
}

export default ConfirmDetails